import { HTTPError } from 'ky';
import {
  capitalize,
  castArray,
  isPlainObject,
  isString,
  lowerCase,
} from 'lodash-es';
import type {
  FieldKeyedErrors,
  StructuredPartyActionError,
} from 'types/api-payloads';

/**
 * Collects the non-empty messages from an error entry: a string, a
 * `{ message }` object, or any nesting of lists of those.
 * @param {unknown} entry - One value from a failed action's `errors`
 * @return {string[]} The messages found, in order
 */
const entryMessages = (entry: unknown): string[] => {
  if (typeof entry === 'string') return entry ? [entry] : [];
  if (Array.isArray(entry)) return entry.flatMap(entryMessages);
  if (entry && typeof entry === 'object' && 'message' in entry) {
    return entryMessages(entry.message);
  }
  return [];
};

/**
 * A failed action's body read as one message: a 409 carries `error`, a 422 is
 * keyed by field, and an application error is a list of `{ code, message }`.
 * @param {StructuredPartyActionError | undefined} err - The failed action's response body
 * @param {string} fallback - The message when the body carries none
 * @param {{ all?: boolean }} options - `all` joins every listed message instead of taking the first
 * @return {string} The message to show
 */
export const actionErrorMessage = (
  err: StructuredPartyActionError | undefined,
  fallback: string,
  { all = false }: { all?: boolean } = {}
): string => {
  const listed = entryMessages(Object.values(err?.errors ?? {}));
  if (all && listed.length) return listed.join('; ');
  return err?.error || listed[0] || fallback;
};

/**
 * The response a failed call carries: an HTTPError's, or any error's
 * `response` that reads as JSON.
 * @param {unknown} error - What the call threw
 * @return {Pick<Response, 'json'> | null} The response, or null without one
 */
const failedResponse = (error: unknown): Pick<Response, 'json'> | null => {
  if (error instanceof HTTPError) return error.response;
  const response = (error as { response?: Partial<Response> } | null)?.response;
  return typeof response?.json === 'function'
    ? (response as Pick<Response, 'json'>)
    : null;
};

/**
 * The message a failed call carries: its body read as a failed action's,
 * else the fallback (no response, or a body that is not JSON or says nothing).
 * @param {unknown} error - What the call threw
 * @param {string} fallback - The message when the response carries none
 * @return {Promise<string>} The message to show
 */
export const apiErrorMessage = async (
  error: unknown,
  fallback: string
): Promise<string> => {
  const response = failedResponse(error);
  if (!response) return fallback;
  const body = (await response
    .json()
    .catch(() => null)) as StructuredPartyActionError | null;
  return actionErrorMessage(body ?? undefined, fallback);
};

/**
 * Separates a field-keyed 422's messages for the fields a form renders inline
 * from everything else, which still belongs in a banner.
 * @param {StructuredPartyActionError | undefined} err - The failed action's response body
 * @param {readonly string[]} fields - The field names the form can show an error on
 * @return {{ fieldErrors: FieldKeyedErrors, rest: StructuredPartyActionError | undefined }} The inline errors, and the body left for the banner (undefined when nothing is left)
 */
export const splitFieldErrors = (
  err: StructuredPartyActionError | undefined,
  fields: readonly string[]
): {
  fieldErrors: FieldKeyedErrors;
  rest: StructuredPartyActionError | undefined;
} => {
  const keyed = err?.errors;
  if (!keyed || Array.isArray(keyed)) return { fieldErrors: {}, rest: err };

  const fieldErrors: FieldKeyedErrors = {};
  const others: FieldKeyedErrors = {};
  Object.entries(keyed).forEach(([field, messages]) => {
    (fields.includes(field) ? fieldErrors : others)[field] = messages;
  });
  if (!Object.keys(fieldErrors).length) return { fieldErrors, rest: err };

  // A nested record's summary ("Concrete entity is invalid") says nothing more
  // once each of its field errors is on its input.
  Object.keys(others).forEach((field) => {
    const prefix = `${field}.`;
    const placed = Object.keys(fieldErrors).some((k) => k.startsWith(prefix));
    const left = Object.keys(others).some((k) => k.startsWith(prefix));
    if (placed && !left) delete others[field];
  });

  const rest: StructuredPartyActionError = { ...err };
  if (Object.keys(others).length) rest.errors = others;
  else delete rest.errors;
  return { fieldErrors, rest: Object.keys(rest).length ? rest : undefined };
};

interface ErrorBody {
  errors?: unknown;
  error?: unknown;
}

/**
 * Whether `value` is a string with more than whitespace in it.
 *
 * @param {unknown} value - Any value from an error body.
 * @return {boolean} True for a non-blank string.
 */
const isPresent = (value: unknown): value is string =>
  isString(value) && value.trim() !== '';

/**
 * The message a refused request's body carries, else `fallback`. Reads Rails
 * field errors (`{ errors: { configuration: ['must…'] } }`) as full messages
 * ("Configuration must…"), an `errors` list of sentences or `{ message }`
 * objects, then a single `{ error }` sentence. Blank messages and anything
 * else are skipped.
 *
 * @param {unknown} body - The parsed error body, if the request got one.
 * @param {string} fallback - Shown when the body names nothing.
 * @return {string} The message to show.
 */
export const errorBodyMessage = (body: unknown, fallback: string): string => {
  const { errors, error } = (body ?? {}) as ErrorBody;
  const messages: unknown[] = Array.isArray(errors)
    ? errors.map((entry: unknown) =>
        isString(entry)
          ? entry
          : (entry as { message?: unknown } | null)?.message
      )
    : Object.entries(
        (isPlainObject(errors) ? errors : {}) as Record<string, unknown>
      ).flatMap(([field, list]) =>
        castArray(list)
          .filter(isPresent)
          .map((message) =>
            field === 'base'
              ? message
              : `${capitalize(lowerCase(field))} ${message}`
          )
      );

  return (
    messages.filter(isPresent).join('; ') ||
    (isPresent(error) ? error : '') ||
    fallback
  );
};
